import Reflux from "reflux";
import _ from "lodash";
import Immutable from "immutable";

import AppConstants from "../constants/AppConstants";
import { getSiteData, getSiteState } from "../hooks/query/useSite";
import UIActions from "../actions/UIActions";
import SitePublishActions from "../actions/SitePublishActions";
import SiteService from "../service/SiteService.mjs";
import APIError from "../service/APIError";
import ErrorUtils from "../utils/ErrorUtils";
import DesignSaveStore from "./DesignSaveStore";
import { isActive } from "../hooks/query/useAccount";
import UserActions from "../actions/UserActions";
import * as Analytics from "@/adaptors/AnalyticsAdaptors";
import { queryClient } from "@/utils/QueryUtils";
import { RENDERER_LOCK_ELIGIBILITY } from "@/constants/QueryKeys";

/** @typedef {import("@/hooks/query/useSite").SiteState} SiteState */

/**
 * This is store is used by Site Publish Panel
 */

var PublishStore = Reflux.createStore({
  storeName: "PublishStore",
  listenables: [SitePublishActions],

  init() {
    this.settings = {};

    this.designSaveListener = null;

    this.canPublish = false;
    this.publishOpen = false;
    this.publishing = false;
    this.publishingBlog = false;

    this.publishBlog = false;

    /**
     * Captured once per flow so started and completed agree. Build it from the
     * local `hasBlog` const: this.settings.hasBlog gets overwritten mid-flow.
     * @type {?Object}
     */
    this.trackedPublishContext = null;
  },

  _onSaveStateChange(inSync) {
    if (this.canPublish === false && inSync) {
      this.canPublish = true;

      if (this.designSaveListener != null) {
        this.designSaveListener.stop();
        this.designSaveListener = null;
      }

      var view = AppConstants.PUBLISH_VIEW_INITIAL;
      if (!isActive()) {
        view = AppConstants.PUBLISH_VIEW_SUBSCRIBE;
      }

      this.settings = this.settings.setIn(["process", "view"], view);
      this.trigger(this.settings);
    }
  },

  onChangeView(view) {
    this.settings = this.settings.setIn(["process", "view"], view);
    this.trigger(this.settings);
  },

  onOpenDialog() {
    if (!this.publishing) {
      var view = AppConstants.PUBLISH_VIEW_INITIAL;

      this.canPublish = DesignSaveStore.getState();

      if (!this.canPublish) {
        view = AppConstants.PREVIEW_VIEW_SAVING;
        this.designSaveListener = this.listenTo(
          DesignSaveStore,
          this._onSaveStateChange,
        );
        DesignSaveStore.onSaveSite(); //force the design to save now.
      } else {
        if (!isActive()) {
          view = AppConstants.PUBLISH_VIEW_SUBSCRIBE;
        }
      }

      var site = getSiteState();
      var hasBlog = false;

      var wpHostId = site.get("wp_host_id");
      if (_.isNumber(wpHostId)) {
        hasBlog = true;
      }

      var initState = Immutable.fromJS({
        process: {
          view: view,
          error: false,
          sitePublished: false,
          blogPublished: false,
        },
        site: site,
        hasBlog: hasBlog,
      });

      this.settings = initState;
    }

    this.publishOpen = true;
    UIActions.modalOpen(AppConstants.MODAL_DESIGN_PUBLISH);
  },

  onCloseDialog() {
    this.publishOpen = false;

    if (this.designSaveListener != null) {
      this.designSaveListener.stop();
      this.designSaveListener = null;
    }

    UIActions.modalClose(AppConstants.MODAL_DESIGN_PUBLISH);
  },

  /**
   * @returns {Object}
   */
  _siteEventProps() {
    return {
      ...this.trackedPublishContext,
      publishedBlog: this.publishBlog,
    };
  },

  /**
   * No publishedBlog: reaching a blog event already means the blog was published.
   * @returns {Object}
   */
  _blogEventProps() {
    return this.trackedPublishContext;
  },

  /**
   *
   * @param {Object} publishSettings
   * @param {boolean=} publishSettings.publishBlog
   */
  onPublishSite(publishSettings) {
    this.publishing = true;

    this.publishBlog = _.get(publishSettings, "publishBlog", false);

    const hasBlog = this.settings.get("hasBlog");

    /** @type {SiteState} */
    const siteState = getSiteData();

    if (!siteState) return;

    this.trackedPublishContext = {
      rendererVersion:
        siteState.renderer === AppConstants.RENDERER_V2
          ? AppConstants.RENDERER_V2
          : 1,
      siteDesignId: siteState.site_design_id,
      hasBlog,
    };

    //If they choose not to publish to the blog continue process as if the don't have a blog
    if (hasBlog && !this.publishBlog) {
      this.settings = this.settings.set("hasBlog", false);
    }

    /**
     * skip first site publish when publishing v2 blog and skip straight to the blog
     * publish. Bail before changing views.
     */
    if (
      hasBlog &&
      this.publishBlog &&
      siteState.renderer === AppConstants.RENDERER_V2
    ) {
      this.publishing = false;
      Analytics.sitePublishStarted(this._siteEventProps());
      return this.onPublishBlog();
    }

    this.settings = this.settings.setIn(
      ["process", "view"],
      AppConstants.PUBLISH_VIEW_PUBLISHING,
    );

    const { renderer, ...siteObj } = siteState;
    const publishEndpoint = (
      renderer === AppConstants.RENDERER_V2
        ? SiteService.publishSiteV2
        : SiteService.publishSite
    ).bind(SiteService);

    Analytics.sitePublishStarted(this._siteEventProps());

    publishEndpoint(siteObj)
      .then((data) => SitePublishActions.publishComplete(data))
      .catch(this.onPublishFail.bind(this));

    this.trigger(this.settings);
  },

  onPublishBlog() {
    this.publishingBlog = true;

    this.settings = this.settings.setIn(
      ["process", "view"],
      AppConstants.PUBLISH_VIEW_PUBLISH_BLOG,
    );

    /** @type {SiteState} */
    const siteState = getSiteData();

    if (!siteState) return;

    const { renderer, ...siteObj } = siteState;

    const publishEndpoint = (
      renderer === AppConstants.RENDERER_V2
        ? SiteService.publishBlogV2
        : SiteService.publishBlog
    ).bind(SiteService);

    Analytics.blogPublishStarted(this._blogEventProps());

    publishEndpoint(siteObj)
      .then(this.onPublishBlogComplete.bind(this))
      .catch(this.onPublishFail.bind(this));

    this.trigger(this.settings);
  },

  onPublishBlogComplete(data) {
    this.publishingBlog = false;

    var blogPublished = false;

    if (data.published) {
      blogPublished = true;
      Analytics.blogPublishCompleted(this._blogEventProps());
    } else {
      // No errorStatus: this error is `{ message }` only — a blog failure inside a
      // 200 response, not an HTTP error.
      Analytics.blogPublishFailed({
        ...this._blogEventProps(),
        error: _.get(data, "error.message"),
      });

      if (_.has(data, "error")) {
        this.settings = this.settings.setIn(
          ["process", "error"],
          Immutable.fromJS(data.error),
        );
      }
    }

    this.settings = this.settings.setIn(
      ["process", "blogPublished"],
      blogPublished,
    );
    // accounts for v2 blog publish bypass
    if (
      this.trackedPublishContext?.rendererVersion === AppConstants.RENDERER_V2
    ) {
      this.settings = this.settings.setIn(["process", "sitePublished"], true);
      Analytics.sitePublishCompleted(this._siteEventProps());
    }

    this.settings = this.settings.setIn(
      ["process", "view"],
      AppConstants.PUBLISH_VIEW_COMPLETE,
    );

    this.trigger(this.settings);
  },

  /**
   * @param {Awaited<ReturnType<typeof SiteService.publishSite>>} data
   */
  onPublishComplete(data) {
    this.publishing = false;

    this.settings = this.settings.setIn(["process", "sitePublished"], true);

    Analytics.sitePublishCompleted(this._siteEventProps());

    if (this.publishBlog) {
      this.onPublishBlog();
    } else {
      this.settings = this.settings.setIn(
        ["process", "view"],
        AppConstants.PUBLISH_VIEW_COMPLETE,
      );
      this.trigger(this.settings);
    }

    queryClient.invalidateQueries({
      queryKey: [RENDERER_LOCK_ELIGIBILITY, data?.publication?.site_design_id],
    });
  },

  onPublishFail(err) {
    const failedDuringBlog = this.publishingBlog;

    this.publishing = false;
    this.publishingBlog = false;

    var error = ErrorUtils.getFormattedError(err);

    const failureProps = {
      error: error.message,
      errorCode: error.code,
      errorStatus: error.status,
    };

    if (failedDuringBlog) {
      Analytics.blogPublishFailed({
        ...this._blogEventProps(),
        ...failureProps,
      });
    }
    /**
     * A v2 blog-stage failure is always the bypass, where publishBlogV2 publishes
     * the site and blog together — so the site failed too. Mirrors the bypass
     * emitting both started events.
     */
    if (
      !failedDuringBlog ||
      this.trackedPublishContext?.rendererVersion === AppConstants.RENDERER_V2
    ) {
      Analytics.sitePublishFailed({
        ...this._siteEventProps(),
        ...failureProps,
      });
    }

    if (APIError.handle(err)) {
      return;
    }

    this.settings = this.settings.setIn(
      ["process", "error"],
      Immutable.fromJS(error),
    );
    this.settings = this.settings.setIn(
      ["process", "view"],
      AppConstants.PUBLISH_VIEW_ERROR,
    );

    this.trigger(this.settings);
  },

  getInitialState() {
    return this.settings;
  },
});

export default PublishStore;
